<template>
  <div class="modal-container">
    <WdsSelect
      v-model="selectedJobs"
      :label="$t('Select jobs')"
      multi-select
      can-deselect
      :list="jobList"
      :load-more-distance="10"
      @toggle-select="open = !open"
      @load-more="loadMoreJobs"
      @search="jobSearch = $event"
    >
      <template #header="{ options, search }">
        <span @click="inputFocus = true">
          <WdsSpinner v-if="loadingJobs" class="inline" indeterminate size="16" />
          <WdsIcon v-else name="search" class="inline" :size="16" />
          <span v-if="options.length && !inputFocus">
            {{ $t(':jobs selected', { jobs: options.length }) }}
          </span>
          <input
            ref="input"
            type="text"
            class="tw-border-0 tw-w-[75%]"
            :placeholder="options.length === 0 ? $t('Search for jobs') : ''"
            @keyup.prevent
            @input.prevent="search"
          />
        </span>
      </template>

      <template #option="{ option }">
        <div class="wds-text">
          <div>
            {{ option.label }}
          </div>
          <div class="tw-text-gray-600">
            {{ option.subtext }}
          </div>
        </div>
      </template>
    </WdsSelect>

    <small v-if="jobList.length === 0 && !loadingJobs" class="tw-my-2">
      {{
        jobSearch.length > 0 ? $t('We found no jobs matching your search') : $t('We found no jobs')
      }}.
      <a :href="laravelNamedRoute('frontend.flows.hiring.actions')">{{ $t('Create a new job post or a direct hire now') }}</a>
      {{ $t('and add it to your project.') }}
    </small>

    <div v-if="selectedJobs.length" class="selected-jobs">
      <WdsText class="tw-mb-2 tw-mt-4" tertiary>Selected jobs ({{ selectedJobs.length }})</WdsText>
      <div class="selected-job-items">
        <div v-for="job in selectedJobs" :key="job.id" class="selected-job-item">
          <WdsStack justify-space-between align-center>
            <WdsText class="tw-mr-2" :text="job.label" :sub-text="job.subtext" />

            <WdsButtonIcon transparent-background @click="removeJob(job)">
              <WdsIconCloseSmall />
            </WdsButtonIcon>
          </WdsStack>
        </div>
      </div>
    </div>
  </div>

  <WdsStack class="tw-mt-16" justify-space-between>
    <WdsButton tertiary @click="$emit('cancel')">{{ $t('Cancel') }}</WdsButton>

    <WdsStack gap-sm align-center>
      <WdsSpinner v-if="submitLoading" indeterminate />
      <WdsButton primary :disabled="submitLoading" @click="submit">{{ $t('Add jobs') }}</WdsButton>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsStack,
  WdsSpinner,
  WdsSelect,
  WdsText,
  WdsButtonIcon,
  WdsIconCloseSmall,
  WdsIcon,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { computed, ref, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { useFocus } from '@vueuse/core'
import { flat } from '@/utils/tools'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { NetworkError } from '@/utils/notification'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
})

type Job = {
  id
  label
  subtext
}

const input = ref()
const { focused: inputFocus } = useFocus(input)

const open = ref(false)

const emits = defineEmits(['cancel', 'addJobs'])

const selectedJobs = ref<Job[]>([])
const jobSearch = ref('')

function removeJob(job) {
  selectedJobs.value = selectedJobs.value.filter((jobItem) => jobItem.id !== job.id)
}

// When WdsSelects resets its internal search we would like to reflect that change by resetting the input search,
// but jobSearch is tied up on an debounce so we don't use :value="jobSearch" but instead check if its value is reset
// and then simply remove the current search. This is a ux design choice and can be considered for removal if we don't
// like the effect.
watch(jobSearch, (value) => {
  if (value === '') {
    input.value.value = ''
  }
})

function jobSubtext(option) {
  return {
    ...option,
    subtext: `
    ${option.startDate ?? $t('No start date')} - ${option.endDate ?? $t('No end date')}
    / ${
      option.internalBudget
        ? `${option.currency} ${option.internalBudget} budget`
        : $t('No budget set')
    }
    ${option.hires?.data?.length ? `/ ${option.hires.data[0].worker.name}` : ''}
  `,
  }
}

const { mutate: attachJobsToProject, loading: submitLoading } = useMutation(
  gql`
    mutation AttachJobsToProject($input: AttachJobsToProjectInput!) {
      attachJobsToProject(input: $input) {
        id
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

async function submit() {
  try {
    await attachJobsToProject({
      input: {
        jobs: selectedJobs.value.map((job) => job.id),
        project: props.project.id,
      },
    })

    emits('addJobs', selectedJobs.value)
  } catch (ex: any) {
    new NetworkError({ ex, msg: $t('An error occurred with saving your changes') }).dispatch()
  }
}

const {
  result,
  loading: loadingJobs,
  fetchMore,
} = useQuery(
  gql`
    query jobs(
      $accounts: [ID!]
      $page: Int!
      $count: Int!
      $completed: Boolean!
      $project: Boolean
      $search: String
    ) {
      jobs(
        accounts: $accounts
        first: $count
        page: $page
        completed: $completed
        hasProject: $project
        search: $search
      ) {
        data {
          id
          value: id
          label: name
          startDate
          endDate
          currency
          internalBudget
          hires {
            data {
              worker {
                name
              }
            }
          }
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  `,
  {
    accounts: [currentAccount.globalId],
    page: 1,
    count: 50,
    completed: false,
    project: false,
    search: jobSearch,
  },
  {
    clientId: 'publicApiCustomFields',
    debounce: 100,
  },
)

function loadMoreJobs() {
  if (!loadingJobs.value) {
    // This logic prevents accidentally loadingJobs in an old page of data resulting in duplicated data
    const { hasMorePages, currentPage } = flat(result ?? {}) ?? {}
    if (!hasMorePages) return

    fetchMore({
      variables: {
        page: currentPage + 1,
      },
    })
  }
}

let previousResult = {}
const jobList = computed(() => {
  const res = loadingJobs.value ? previousResult : result.value
  previousResult = res
  const { data: jobs } = res?.jobs ?? {}

  return jobs?.map(jobSubtext) ?? []
})
</script>

<style lang="scss" scoped>

.inline {
  @apply tw-inline;
  @apply tw-mr-1.5 tw-mb-0.5;
}

.modal-container {
  @apply tw-max-h-[600px];
  @apply tw-overflow-scroll;
}
.jobs {
  @apply tw-max-h-[350px];
  @apply tw-overflow-y-scroll;
}
.selected-job-items {
  @apply tw-max-h-[230px];
  @apply tw-overflow-y-scroll;
  @apply tw-relative;
}

// The deign calls for a mask like this, but it is too difficult to figure out how to remove
// it when getting close to the bottom during scrolling so i'll leave it like this for potential
// future use or if pablo insists.
.selected-job-items-mask {
  --mask: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0,
      rgba(0, 0, 0, 1) 40%,
      rgba(0, 0, 0, 0) 95%,
      rgba(0, 0, 0, 0) 0
    )
    100% 50% / 100% 100% repeat-x;
  -webkit-mask: var(--mask);
  mask: var(--mask);
}

.selected-job-item {
  border: 1px solid lightgrey;
  border-bottom: none;
  padding: 12px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  & + .selected-job-item {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &:last-child {
    border-bottom: 1px solid lightgrey;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
}
:deep(.wds-select-list-container) {
  @apply tw-fixed;
  @apply tw-w-[492px];
}
</style>
